import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface JobGovernanceCapacity {
  governed: boolean;
  is_unlimited?: boolean;
  base_limit?: number;
  replacement_limit?: number;
  base_consumed?: number;
  replacement_consumed?: number;
  total_consumed?: number;
  credits_generated?: number;
  remaining?: number;
  has_overrides?: boolean;
  base_override_total?: number;
  replacement_override_total?: number;
  shortlist_pass_limit?: number;
  shortlist_passes_consumed?: number;
  remaining_shortlist_capacity?: number;
}

export interface ApplicationPassState {
  pass_consumed: boolean;
  pass_source: 'base' | 'replacement' | 'wallet_credit';
  replacement_credit_generated: boolean;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function optionalNumber(record: Record<string, unknown>, key: string): number | undefined {
  const value = record[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function optionalBoolean(record: Record<string, unknown>, key: string): boolean | undefined {
  const value = record[key];
  return typeof value === 'boolean' ? value : undefined;
}

function parseJobGovernanceCapacity(value: unknown): JobGovernanceCapacity | null {
  if (!isRecord(value) || typeof value.governed !== 'boolean') return null;

  return {
    governed: value.governed,
    is_unlimited: optionalBoolean(value, 'is_unlimited'),
    base_limit: optionalNumber(value, 'base_limit'),
    replacement_limit: optionalNumber(value, 'replacement_limit'),
    base_consumed: optionalNumber(value, 'base_consumed'),
    replacement_consumed: optionalNumber(value, 'replacement_consumed'),
    total_consumed: optionalNumber(value, 'total_consumed'),
    credits_generated: optionalNumber(value, 'credits_generated'),
    remaining: optionalNumber(value, 'remaining'),
    has_overrides: optionalBoolean(value, 'has_overrides'),
    base_override_total: optionalNumber(value, 'base_override_total'),
    replacement_override_total: optionalNumber(value, 'replacement_override_total'),
    shortlist_pass_limit: optionalNumber(value, 'shortlist_pass_limit'),
    shortlist_passes_consumed: optionalNumber(value, 'shortlist_passes_consumed'),
    remaining_shortlist_capacity: optionalNumber(value, 'remaining_shortlist_capacity'),
  };
}

function isPassSource(value: string): value is ApplicationPassState['pass_source'] {
  return value === 'base' || value === 'replacement' || value === 'wallet_credit';
}

export function useJobGovernance(jobId: string | undefined) {
  return useQuery({
    queryKey: ['job-governance', jobId],
    queryFn: async (): Promise<JobGovernanceCapacity | null> => {
      const { data, error } = await supabase.rpc('get_job_pass_capacity', {
        _job_id: jobId!,
      });
      if (error) throw error;

      const raw = parseJobGovernanceCapacity(data);
      if (!raw) return raw;

      if (raw.shortlist_pass_limit === undefined) {
        raw.shortlist_pass_limit = raw.base_limit;
      }
      if (raw.shortlist_passes_consumed === undefined) {
        raw.shortlist_passes_consumed = raw.base_consumed;
      }
      if (raw.remaining_shortlist_capacity === undefined) {
        raw.remaining_shortlist_capacity = raw.remaining;
      }

      return raw;
    },
    enabled: !!jobId,
    staleTime: 10_000,
  });
}

export function useApplicationPassState(applicationId: string | undefined) {
  return useQuery({
    queryKey: ['application-pass-state', applicationId],
    queryFn: async (): Promise<ApplicationPassState> => {
      const { data, error } = await supabase
        .from('application_governed_passes')
        .select('pass_consumed, pass_source, replacement_credit_generated')
        .eq('application_id', applicationId!)
        .maybeSingle();

      if (error) throw error;

      if (!data) return {
        pass_consumed: false,
        pass_source: 'base',
        replacement_credit_generated: false,
      };

      return {
        pass_consumed: data.pass_consumed,
        pass_source: isPassSource(data.pass_source) ? data.pass_source : 'base',
        replacement_credit_generated: data.replacement_credit_generated,
      };
    },
    enabled: !!applicationId,
  });
}
